import * as DataUtils from 'utils/data'
import DataTypes from 'constants/DataTypes'
import DataLimits from 'constants/DataLimits'

// The two preset alert levels the user can pick in the values step.
export const YELLOW_LEVEL = 'yellow'
export const RED_LEVEL = 'red'

// Translation key for the label shown on the level button and menu.
export const LEVEL_LABEL_KEYS = {
  [YELLOW_LEVEL]: 'yellowWarning',
  [RED_LEVEL]: 'redWarning'
}

const yellowLimits = measurementUnits => ({
  iaq: DataLimits.INDICATORS_YELLOW,
  covid19: DataLimits.INDICATORS_YELLOW,
  thermalIndicator: DataLimits.INDICATORS_YELLOW,
  ventilationIndicator: DataLimits.INDICATORS_YELLOW,
  moldIndicator: DataLimits.INDICATORS_YELLOW,
  tempLow: DataUtils.formatData(20, DataTypes.TEMPERATURE, measurementUnits),
  tempHigh: DataUtils.formatData(23, DataTypes.TEMPERATURE, measurementUnits),
  humLow: '40',
  humHigh: '60',
  co2: DataLimits.CO2_LOW,
  radon: DataLimits.RADON_LOW,
  formaldehyde: DataUtils.formatData(
    DataLimits.FORMALDEHYDE_LOW,
    DataTypes.FORMALDEHYDE,
    measurementUnits
  ),
  vocs: DataUtils.formatData(DataLimits.VOCS_LOW, DataTypes.VOCS, measurementUnits),
  vocsIndex: DataUtils.formatData(
    DataLimits.VOCS_INDEX_LOW,
    DataTypes.VOCS_INDEX,
    measurementUnits
  ),
  pm10: DataUtils.formatData(DataLimits.PM10_LOW, DataTypes.PM10, measurementUnits),
  pm25: DataUtils.formatData(DataLimits.PM25_LOW, DataTypes.PM25, measurementUnits),
  pm1: DataUtils.formatData(DataLimits.PM1_LOW, DataTypes.PM1, measurementUnits),
  pm4: DataUtils.formatData(DataLimits.PM4_LOW, DataTypes.PM4, measurementUnits),
  nox: DataLimits.NOX_LOW,
  no2: DataLimits.NO2_LOW,
  o3: DataUtils.formatData(DataLimits.O3_LOW, DataTypes.O3, measurementUnits),
  co: DataUtils.formatData(DataLimits.CO_LOW, DataTypes.CO, measurementUnits),
  // Noise is measured in dB only, so there is nothing to convert. The key is
  // computed because the data type is dB, with a capital B, and written
  // plainly it reads like a typo.
  [DataTypes.NOISE]: DataLimits.NOISE_LOW
})

const redLimits = measurementUnits => ({
  iaq: DataLimits.INDICATORS_RED,
  covid19: DataLimits.INDICATORS_RED,
  thermalIndicator: DataLimits.INDICATORS_RED,
  ventilationIndicator: DataLimits.INDICATORS_RED,
  moldIndicator: DataLimits.INDICATORS_RED,
  tempLow: DataUtils.formatData(18, DataTypes.TEMPERATURE, measurementUnits),
  tempHigh: DataUtils.formatData(26, DataTypes.TEMPERATURE, measurementUnits),
  humLow: '30',
  humHigh: '70',
  co2: DataLimits.CO2_HIGH,
  radon: DataLimits.RADON_HIGH,
  formaldehyde: DataUtils.formatData(
    DataLimits.FORMALDEHYDE_HIGH,
    DataTypes.FORMALDEHYDE,
    measurementUnits
  ),
  vocs: DataUtils.formatData(DataLimits.VOCS_HIGH, DataTypes.VOCS, measurementUnits),
  vocsIndex: DataUtils.formatData(
    DataLimits.VOCS_INDEX_HIGH,
    DataTypes.VOCS_INDEX,
    measurementUnits
  ),
  pm10: DataUtils.formatData(DataLimits.PM10_HIGH, DataTypes.PM10, measurementUnits),
  pm25: DataUtils.formatData(DataLimits.PM25_HIGH, DataTypes.PM25, measurementUnits),
  pm1: DataUtils.formatData(DataLimits.PM1_HIGH, DataTypes.PM1, measurementUnits),
  pm4: DataUtils.formatData(DataLimits.PM4_HIGH, DataTypes.PM4, measurementUnits),
  nox: DataLimits.NOX_HIGH,
  no2: DataLimits.NO2_HIGH,
  o3: DataUtils.formatData(DataLimits.O3_HIGH, DataTypes.O3, measurementUnits),
  co: DataUtils.formatData(DataLimits.CO_HIGH, DataTypes.CO, measurementUnits),
  [DataTypes.NOISE]: DataLimits.NOISE_HIGH
})

// Form fields hold strings. Some limits are numbers, so they are converted
// here. Before, they reached the payload through the DOM and were already
// strings by the time the form was submitted.
const toFieldValue = limit => (limit === null || limit === undefined ? '' : String(limit))

/**
 * The preset values for one level, limited to the parameters the alert uses.
 * Returns an empty object for an unknown level.
 */
export const getLevelLimits = (level, dataTypes, measurementUnits) => {
  if (level !== YELLOW_LEVEL && level !== RED_LEVEL) return {}

  const limits =
    level === YELLOW_LEVEL ? yellowLimits(measurementUnits) : redLimits(measurementUnits)

  return dataTypes.reduce(
    (selected, dataType) => ({ ...selected, [dataType]: toFieldValue(limits[dataType]) }),
    {}
  )
}
